export type Dpi = 72 | 150 | 300

export const DPI_VALUES: readonly Dpi[] = [72, 150, 300]

export function dpiLabel(dpi: Dpi): string {
  return `${dpi} DPI`
}

export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const SOFT_WARN_PAGES = 100
const JPEG_QUALITY = 0.85

export type ImageOutputFormat = 'jpg' | 'png'

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function imageFilename(
  originalName: string,
  page: number,
  totalPages: number,
  format: ImageOutputFormat,
): string {
  const dot = originalName.lastIndexOf('.')
  const base = dot !== -1 ? originalName.slice(0, dot) : originalName
  if (totalPages === 1) return `${base}.${format}`
  const width = String(totalPages).length
  return `${base}-page-${String(page).padStart(width, '0')}.${format}`
}

export function zipFilename(originalName: string): string {
  const dot = originalName.lastIndexOf('.')
  const base = dot !== -1 ? originalName.slice(0, dot) : originalName
  return `${base}.zip`
}

export function shouldSoftWarn(totalPages: number): boolean {
  return totalPages > SOFT_WARN_PAGES
}

export function scaleForDpi(dpi: Dpi): number {
  // pdf.js viewports are sized at 72 DPI by default
  return dpi / 72
}

export interface RenderProgress {
  current: number
  total: number
}

export interface RenderResult {
  blobs: Blob[]
  previewDataUrl: string
  totalBytes: number
}

function canvasToImageBlob(
  canvas: HTMLCanvasElement,
  format: ImageOutputFormat,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (format === 'png') {
      canvas.toBlob((blob) => resolve(blob), 'image/png')
      return
    }

    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', JPEG_QUALITY)
  })
}

export interface RenderOptions {
  file: File
  dpi: Dpi
  format: ImageOutputFormat
  signal: AbortSignal
  onProgress: (p: RenderProgress) => void
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (n: number) => Promise<PdfJsPage>
  destroy: () => Promise<void>
}

interface PdfJsPage {
  getViewport: (opts: { scale: number }) => PdfJsViewport
  render: (opts: { canvasContext: CanvasRenderingContext2D; viewport: PdfJsViewport }) => {
    promise: Promise<void>
    cancel: () => void
  }
  cleanup: () => void
}

interface PdfJsViewport {
  width: number
  height: number
}

/**
 * Render every page of the PDF to an image blob. Throws on cancel via AbortSignal.
 *
 * Pages are rendered sequentially and the canvas is sized to fit each page, so
 * peak memory stays bounded by one page at a time. Strategy chosen because
 * iOS Safari OOMs around 250 MB of decoded image data — we cannot hold N
 * canvases simultaneously for large documents.
 */
export async function renderPdfToImages(options: RenderOptions): Promise<RenderResult> {
  const { file, dpi, format, signal, onProgress } = options

  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const bytes = await file.arrayBuffer()
  const doc = await pdfjs.getDocument({ data: bytes }).promise

  try {
    const totalPages = doc.numPages
    const blobs: Blob[] = []
    let totalBytes = 0
    let previewDataUrl = ''
    const scale = scaleForDpi(dpi)

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas-unavailable')

    onProgress({ current: 0, total: totalPages })

    for (let i = 1; i <= totalPages; i++) {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

      const page = await doc.getPage(i)
      const viewport = page.getViewport({ scale })
      canvas.width = Math.ceil(viewport.width)
      canvas.height = Math.ceil(viewport.height)

      const task = page.render({ canvasContext: ctx, viewport })
      const onAbort = () => task.cancel()
      signal.addEventListener('abort', onAbort, { once: true })
      try {
        await task.promise
      } finally {
        signal.removeEventListener('abort', onAbort)
      }

      if (i === 1) {
        // ~200px wide preview is plenty for the result panel
        previewDataUrl =
          format === 'png'
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', 0.6)
      }

      const blob = await canvasToImageBlob(canvas, format)
      if (!blob) throw new Error('encode-failed')

      blobs.push(blob)
      totalBytes += blob.size
      page.cleanup()

      onProgress({ current: i, total: totalPages })
    }

    // Free the working canvas
    canvas.width = 0
    canvas.height = 0

    return { blobs, previewDataUrl, totalBytes }
  } finally {
    await doc.destroy().catch(() => {})
  }
}

/**
 * Pack rendered images into a ZIP archive in-memory.
 * Uses `STORE` to keep browser latency predictable on large multi-page documents.
 */
export async function packZip(
  originalName: string,
  blobs: Blob[],
  format: ImageOutputFormat,
): Promise<Blob> {
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()
  blobs.forEach((blob, i) => {
    const name = imageFilename(originalName, i + 1, blobs.length, format)
    zip.file(name, blob, { compression: 'STORE' })
  })
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}
